import { ComputedRef } from '@vue/composition-api';
import { useStore } from '@/hooks/useStore';
import { BoardEntryConv } from '@/models/settouMessageBoard';
import { createInjection } from '@/lib/createInjection';

interface BoardEntryPermissionsOptions {
  isSuperAdmin: ComputedRef<boolean>;
  isCreatableRole: ComputedRef<boolean>;
  isRepliableRole: ComputedRef<boolean>;
  isUpperOrganization: ComputedRef<boolean>;
}

export interface UseBoardEntryPermissionsResult {
  isEditable: (item: BoardEntryConv) => boolean;
  isDeletable: (item: BoardEntryConv) => boolean;
  canCreateBoardEntry: () => boolean;
}

const { provide, inject } =
  createInjection<UseBoardEntryPermissionsResult>('settouMessageBoard/useBoardEntryPermissions');

export function provideBoardEntryPermissions(options: BoardEntryPermissionsOptions): void {
  provide(buildBoardEntryPermissions(options));
}

export function useBoardEntryPermissions(): UseBoardEntryPermissionsResult {
  return inject();
}

// 投稿単位の編集・削除可否の権限判定を担う composable。
function buildBoardEntryPermissions(options: BoardEntryPermissionsOptions): UseBoardEntryPermissionsResult {
  const { isSuperAdmin, isCreatableRole, isRepliableRole, isUpperOrganization } = options;
  const store = useStore();
  const userState = store.state.user;

  // バックエンドのcanCreateBoardEntry = hasBoardRoleCreate || isValidUpperOrganization と一致させる
  // （hasBoardRoleCreate内のsuperAdmin早期returnもここに含める）
  const canCreateBoardEntry = (): boolean => {
    return isSuperAdmin.value || isCreatableRole.value || (isUpperOrganization.value && isRepliableRole.value);
  };

  const isEditable = (item: BoardEntryConv): boolean => {
    if (isSuperAdmin.value || isRepliableRole.value) return true;
    // 現在CREATE権限を持たない場合、自分の投稿であっても更新は不可（バックエンドのcanUpdateBoardEntryが呼ぶcanCreateBoardEntryチェックと一致させる）
    if (!canCreateBoardEntry()) return false;
    if (userState.id === item.created_by) return true;

    // 自分が投稿したものでなくても、伝達経路上位に位置するユーザーが投稿したものである場合は更新可
    // 上位組織のユーザーが連絡事項を投稿し、下位組織のユーザーが内容を更新する運用を想定している
    // いままで上位組織から下位へ直接連絡する手段がないため
    return item.is_created_by_upper_organization;
  };
  // バックエンドのcanDestroyBoardEntry = superAdmin || (created_by === user.id && canCreateBoardEntry) と一致させる
  const isDeletable = (item: BoardEntryConv): boolean => {
    if (isSuperAdmin.value) return true;
    if (!canCreateBoardEntry()) return false;

    return item.created_by === userState.id;
  };

  return {
    isEditable,
    isDeletable,
    canCreateBoardEntry,
  };
}
